/**
 * What the machines would make of a policy before it is published (policyManifest.ts §4): per
 * manifest the organisation's machines run, the errors they would refuse the whole policy for, the
 * settings they would ignore and the keys they do not know; and machines too old to carry a
 * manifest, judged by each clause's `since`. Asked of the platform as the document changes.
 */

import type { PolicyCheckAnswer, PolicyManifest } from '@aioe/shared';
import { useEffect, useState } from 'react';

import { problemText, routes } from '../api/client.ts';
import { policyLabeller } from './policyLabels.ts';
import { StatusMark } from './ui.tsx';

/** Asks the platform what the machines in scope would make of `json`, as it changes. */
export function usePolicyCheck(json: string, projectIds: string[]): { answer?: PolicyCheckAnswer; error?: string } {
  const [answer, setAnswer] = useState<PolicyCheckAnswer>();
  const [error, setError] = useState<string>();
  useEffect(() => {
    let policy: Record<string, unknown>;
    try {
      policy = JSON.parse(json) as Record<string, unknown>;
    } catch {
      return;
    }
    const t = setTimeout(() => {
      routes
        .checkPolicy({ policy, ...(projectIds.length ? { projectIds } : {}) })
        .then((a) => {
          setAnswer(a);
          setError(undefined);
        })
        .catch((e) => setError(problemText(e)));
    }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [json, projectIds.join(',')]);
  return { answer, error };
}

/** Machines in scope that would refuse the whole policy and keep their last one. */
export const refusingCount = (a: PolicyCheckAnswer | undefined): number => (a ? a.digests.filter((d) => d.errors.length).reduce((n, d) => n + d.devices.length, 0) : 0);

/** The oldest AI Workbench build in scope, so a clause shows "needs x.y" only when a machine is too old for it. */
export function oldestVersion(a: PolicyCheckAnswer | undefined): string | undefined {
  if (!a) return undefined;
  const versions = [...a.digests.flatMap((d) => d.devices.map((x) => x.appVersion)), ...a.legacy.map((l) => l.appVersion)].filter(Boolean);
  const cmp = (x: string, y: string) => {
    const px = x.split('.').map(Number);
    const py = y.split('.').map(Number);
    for (let i = 0; i < 3; i++) if ((px[i] ?? 0) !== (py[i] ?? 0)) return (px[i] ?? 0) - (py[i] ?? 0);
    return 0;
  };
  return versions.sort(cmp)[0];
}

export function PolicyCompatibility({ json, projectIds, manifest }: { json: string; projectIds: string[]; manifest?: PolicyManifest }) {
  const { answer, error } = usePolicyCheck(json, projectIds);
  return <CompatibilityReport answer={answer} error={error} manifest={manifest} />;
}

/** The answer in words: a headline with its mark, then each build that would refuse or ignore part of it, named as the workbench names them. */
export function CompatibilityReport({ answer, error, manifest }: { answer?: PolicyCheckAnswer; error?: string; manifest?: PolicyManifest }) {
  const label = policyLabeller(manifest);
  if (error) return <p className="text-xs text-danger">{error}</p>;
  if (!answer) return null;
  if (!answer.devices) return <p className="text-sm text-muted">No machine is in this policy’s scope yet, so nothing can be checked against a build.</p>;
  const refusing = answer.digests.filter((d) => d.errors.length);
  const ignoring = answer.digests.filter((d) => !d.errors.length && (d.settingErrors.length || d.unknownKeys.length));
  const fine = answer.digests.filter((d) => d.held && !d.errors.length && !d.settingErrors.length && !d.unknownKeys.length);
  const unheld = answer.digests.filter((d) => !d.held);
  const legacyOld = answer.legacy.filter((l) => l.tooOldFor.length);
  const names = (devices: Array<{ hostname: string }>) => `${devices.slice(0, 3).map((d) => d.hostname).join(', ')}${devices.length > 3 ? `, and ${devices.length - 3} more` : ''}`;
  const state = refusing.length ? 'error' : ignoring.length || legacyOld.length ? 'medium' : unheld.length ? 'idle' : 'connected';
  const headline = refusing.length
    ? `${refusing.reduce((n, d) => n + d.devices.length, 0)} of ${answer.devices} machines would refuse this policy`
    : ignoring.length || legacyOld.length
      ? `${ignoring.reduce((n, d) => n + d.devices.length, 0) + legacyOld.length} of ${answer.devices} machines are too old for part of it`
      : unheld.length
        ? `${unheld.reduce((n, d) => n + d.devices.length, 0)} of ${answer.devices} machines run a build the platform has not described yet`
        : `Every one of the ${answer.devices} machines in scope would apply this policy as written`;
  return (
    <div className="grid gap-1.5 text-sm">
      <StatusMark state={state} label={headline} />
      <ul className="grid gap-1 text-xs text-muted">
        {refusing.map((d) => (
          <li key={d.digest}>
            <span className="text-danger">AI Workbench {d.versions[0]}</span> ({names(d.devices)}) would refuse the whole policy and keep its last one: {d.errors.slice(0, 3).map((e) => `${label(e.path)}: ${e.message}`).join('; ')}
            {d.errors.length > 3 ? `; and ${d.errors.length - 3} more` : ''}
          </li>
        ))}
        {ignoring.map((d) => (
          <li key={d.digest}>
            AI Workbench {d.versions[0]} ({names(d.devices)})
            {d.unknownKeys.length ? ` does not know ${d.unknownKeys.slice(0, 4).map(label).join(', ')}${d.unknownKeys.length > 4 ? ` and ${d.unknownKeys.length - 4} more` : ''}: kept, ignored` : ''}
            {d.settingErrors.length ? `${d.unknownKeys.length ? ';' : ''} would ignore ${d.settingErrors.map((e) => label(e.path)).join(', ')} (${d.settingErrors[0]!.message})` : ''}
          </li>
        ))}
        {legacyOld.map((l) => (
          <li key={l.deviceId}>
            {l.hostname} (AI Workbench {l.appVersion}, from before manifests) is too old for {l.tooOldFor.map(label).join(', ')}
          </li>
        ))}
        {unheld.map((d) => (
          <li key={d.digest}>
            {names(d.devices)} run {d.versions.join(', ')}, whose description the platform has asked them for; checked once it arrives
          </li>
        ))}
        {fine.length && (refusing.length || ignoring.length) ? <li>{fine.reduce((n, d) => n + d.devices.length, 0)} machines on {fine.map((d) => d.versions[0]).join(', ')} apply it as written.</li> : null}
      </ul>
    </div>
  );
}
